import moment from 'moment';
import React from 'react';
import { Table, Tag, Button, Card, Tooltip } from 'antd';
import { EditOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';

function TableSubjectTeachingRoom(props) {
    const handleEditClick = (scheduleData) => {
        if (props.onEditClick) {
            props.onEditClick(scheduleData);
        }
    };

    const renderScheduleItem = (schedule, sessionColor) => {
        const isExam = schedule.examId || schedule['subjectTeachingName']?.includes('[');

        return (
            <Card
                size="small"
                className={`mb-2 last:mb-0 ${isExam ? 'border-red-300 bg-red-50' : 'border-gray-200'} hover:shadow-md transition-shadow`}
                bordered
                style={{width: '110px'}}
            >
                <div className="flex flex-col gap-2">
                    <div className={`font-medium text-xs ${isExam ? 'text-red-600' : 'text-gray-800'} leading-tight break-words`}>
                        {schedule['subjectTeachingName']}
                    </div>
                    <div className="flex flex-col gap-1">
                        <Tag color="blue" className="text-xs m-0 break-words whitespace-normal">
                            {schedule['teacherName']}
                        </Tag>
                        <Tag color={sessionColor} className="text-xs m-0 whitespace-normal">
                            {moment(schedule.startDateTime).format('HH:mm')} - {moment(schedule.endDateTime).format('HH:mm')}
                        </Tag>
                    </div>
                    {!schedule.examId && (
                        <Tooltip title="Chỉnh sửa lịch học">
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                size="small"
                                onClick={() => handleEditClick(schedule)}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 w-full text-xs px-1"
                            >
                                Sửa
                            </Button>
                        </Tooltip>
                    )}
                </div>
            </Card>
        );
    };

    const renderSessionCell = (schedules, sessionType, sessionColor, bgColor) => {
        if (!schedules || schedules.length === 0) {
            return <div className="text-center text-gray-400 py-4">-</div>;
        }

        return (
            <div className={`${bgColor} min-h-[60px]`}>
                {schedules.map((schedule, idx) => (
                    <React.Fragment key={`${sessionType}-schedule-${idx}`}>
                        {renderScheduleItem(schedule, sessionColor)}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const columns = [
        {
            title: <div className="text-center font-bold">Ngày</div>,
            dataIndex: 'date',
            key: 'date',
            fixed: 'left',
            width: 80,
            render: (date, record, index) => {
                const dayOfWeek = moment(date).day();
                const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

                // Only render on the first session (morning) of each day, merge 3 rows
                const obj = {
                    children: (
                        <div className="text-center font-semibold text-xs">
                            <div>{dayNames[dayOfWeek]}</div>
                            <div className="text-gray-600">{moment(date).format('DD/MM')}</div>
                        </div>
                    ),
                    props: {},
                };

                // Check if this is the morning session (first session of the day)
                if (record.session === 'S') {
                    obj.props.rowSpan = 3; // Merge 3 rows (S, C, T)
                } else {
                    obj.props.rowSpan = 0; // Hide for afternoon and night
                }

                return obj;
            },
        },
        {
            title: <div className="text-center font-bold">Buổi</div>,
            dataIndex: 'session',
            key: 'session',
            fixed: 'left',
            width: 50,
            render: (session) => {
                const sessionConfig = {
                    'S': { text: 'Sáng', icon: 'S' },
                    'C': { text: 'Chiều', icon: 'C' },
                    'T': { text: 'Tối', icon: 'T' }
                };
                const config = sessionConfig[session] || {};
                return (
                    <Tooltip title={config.text}>
                        <div className="text-center font-semibold">
                            <div>{config.icon}</div>
                        </div>
                    </Tooltip>
                );
            },
        },
    ];

    // Add room columns
    if (props.data?.rooms) {
        props.data.rooms.forEach((room, roomIdx) => {
            columns.push({
                title: (
                    <div className="text-center font-bold text-xs">
                        {room.roomName}
                    </div>
                ),
                dataIndex: `room_${roomIdx}`,
                key: `room_${roomIdx}`,
                width: 60,
                render: (_, record) => {
                    const schedules = record[`room_${roomIdx}_schedules`];
                    const sessionColors = {
                        'S': 'blue',
                        'C': 'orange',
                        'T': 'green'
                    };
                    const bgColors = {
                        'S': 'bg-blue-50',
                        'C': 'bg-orange-50',
                        'T': 'bg-green-50'
                    };
                    return renderSessionCell(
                        schedules,
                        record.session,
                        sessionColors[record.session],
                        bgColors[record.session]
                    );
                },
            });
        });
    }

    // Transform data for table
    const tableData = [];
    if (props.data?.times) {
        props.data.times.forEach((time, timeIdx) => {
            const currentDate = moment(time).format('YYYY-MM-DD');

            // Morning
            const morningRow = {
                key: `${timeIdx}-morning`,
                date: time,
                session: 'S',
            };
            props.data.rooms?.forEach((room, roomIdx) => {
                // Filter morning schedules for this specific date
                const morningSchedules = (room.season.morning || []).filter(schedule => {
                    const scheduleDate = moment(schedule.startDateTime).format('YYYY-MM-DD');
                    const scheduleHour = moment(schedule.startDateTime).hour();
                    // Morning is typically 6:00 - 11:59
                    return scheduleDate === currentDate && scheduleHour >= 6 && scheduleHour < 12;
                });
                morningRow[`room_${roomIdx}_schedules`] = morningSchedules;
            });
            tableData.push(morningRow);

            // Afternoon
            const afternoonRow = {
                key: `${timeIdx}-afternoon`,
                date: time,
                session: 'C',
            };
            props.data.rooms?.forEach((room, roomIdx) => {
                // Filter afternoon schedules for this specific date
                const afternoonSchedules = (room.season.afternoon || []).filter(schedule => {
                    const scheduleDate = moment(schedule.startDateTime).format('YYYY-MM-DD');
                    const scheduleHour = moment(schedule.startDateTime).hour();
                    // Afternoon is typically 12:00 - 16:59
                    return scheduleDate === currentDate && scheduleHour >= 12 && scheduleHour < 17;
                });
                afternoonRow[`room_${roomIdx}_schedules`] = afternoonSchedules;
            });
            tableData.push(afternoonRow);

            // Night
            const nightRow = {
                key: `${timeIdx}-night`,
                date: time,
                session: 'T',
            };
            props.data.rooms?.forEach((room, roomIdx) => {
                // Filter night schedules for this specific date
                const nightSchedules = (room.season.night || []).filter(schedule => {
                    const scheduleDate = moment(schedule.startDateTime).format('YYYY-MM-DD');
                    const scheduleHour = moment(schedule.startDateTime).hour();
                    // Night is typically 17:00 - 23:59
                    return scheduleDate === currentDate && scheduleHour >= 17;
                });
                nightRow[`room_${roomIdx}_schedules`] = nightSchedules;
            });
            tableData.push(nightRow);
        });
    }

    return (
        <div className="">
            {props.data != null && (props.data.rooms?.length > 0 || props.data.times?.length > 0) ? (
                <Card
                    title={
                        <div className="text-center">
                            <h3 className="text-2xl font-bold text-gray-800 m-0">
                                📅 DANH SÁCH LỊCH HỌC THEO PHÒNG
                            </h3>
                        </div>
                    }
                    className="shadow-lg"
                >
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        pagination={false}
                        scroll={{ x: 'max-content', y: 550 }}
                        size="small"
                        bordered
                        sticky
                        rowClassName={(record, index) => {
                            const colors = {
                                'S': 'bg-blue-50',
                                'C': 'bg-yellow-50',
                                'T': 'bg-green-100'
                            };
                            return colors[record.session] || '';
                        }}
                        className="custom-schedule-table"
                    />
                    <style>{`
                        .custom-schedule-table .ant-table-thead > tr > th {
                            background: #059669 !important;
                            color: white !important;
                            font-weight: bold;
                            text-align: center;
                            position: sticky !important;
                            top: 0 !important;
                            z-index: 10 !important;
                        }
                        .custom-schedule-table .ant-table-cell {
                            padding: 8px !important;
                        }
                    `}</style>
                </Card>
            ) : (
                <Card className="text-center shadow-lg">
                    <div className="py-8">
                        <div className="text-6xl mb-4">📭</div>
                        <h3 className="text-xl font-semibold text-gray-600">
                            Không có dữ liệu lịch học!
                        </h3>
                    </div>
                </Card>
            )}
        </div>
    );
}

export default TableSubjectTeachingRoom;
